import { useMutation } from '@apollo/client';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import React from 'react';
import { useForm } from 'react-hook-form';
import { CoherencePanelStyles } from '../../../../app/common/styles/CommonStyleObjects';

import {
  GET_COMPANY_CREATION_COMPANY_INFO,
  GET_COMPANY_CREATION_PROGRESS,
  UPDATE_FUNCTIONAL_CURRENCY,
} from '../../../../utils/statApi/CompanyCreationApi';

import ActionButtons from '../../../common/ActionButtons';
import LoadingErrorMessage from '../../../common/errorContent/LoadingErrorMessage';
import { IFunctionalCurrency } from '../types';
import FunctionalCurrencyForm from './FunctionalCurrencyForm';

interface IFunctionalCurrencyEditPanelProps {
  closePanel: () => void;
  data: IFunctionalCurrency;
  companyCreationId: number;
}

const FunctionalCurrencyEditPanel = ({
  closePanel,
  data,
  companyCreationId,
}: IFunctionalCurrencyEditPanelProps): JSX.Element => {
  const form = useForm<IFunctionalCurrency>({
    defaultValues: {
      ...data,
    },
  });

  const [updateFunctionalCurrency, { loading, error }] = useMutation(UPDATE_FUNCTIONAL_CURRENCY, {
    onCompleted: closePanel,
    refetchQueries: [
      {
        query: GET_COMPANY_CREATION_COMPANY_INFO,
        variables: {
          companyCreationId,
        },
      },
      {
        query: GET_COMPANY_CREATION_PROGRESS,
        variables: {
          companyCreationId,
        },
      },
    ],
  });

  const { handleSubmit } = form;

  const handleOnSaveClick = (formData: IFunctionalCurrency): void => {
    const document = !formData.functionalCurrencyDocument
      ? null
      : {
          id: formData.functionalCurrencyDocument.id,
          title: formData.functionalCurrencyDocument.title,
          url: formData.functionalCurrencyDocument.url,
          uploadDate: formData.functionalCurrencyDocument.uploadDate,
          isDeleted: false,
        };

    updateFunctionalCurrency({
      variables: {
        input: {
          id: formData.id || -1,
          companyCreationRequestId: companyCreationId,
          statutoryCurrency: formData.statutoryCurrency,
          functionalCurrency: formData.functionalCurrency,
          functionalCurrencyDocument: document,
        },
      },
    });
  };

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText="Edit Functional Currency"
      isOpen
      onDismiss={closePanel}
      hasCloseButton
      styles={CoherencePanelStyles}
      closeButtonAriaLabel="Close Edit Functional Currency Panel"
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          mutationLoading={loading}
          closePanel={closePanel}
          handleSubmit={handleSubmit(handleOnSaveClick)}
          saveLabel="Save Functional Currency"
          saveTitle="Save Functional Currency"
          cancelLabel="Cancel Functional Currency Edit"
          cancelTitle="Cancel Functional Currency Edit"
        />
      )}
    >
      <form onSubmit={handleSubmit(handleOnSaveClick)}>
        <LoadingErrorMessage error={error} loading={loading} />
        <FunctionalCurrencyForm form={form} data={data} />
      </form>
    </CoherencePanel>
  );
};

export default FunctionalCurrencyEditPanel;
